/**
 * SkiSupply — restore the desktop nav after the cart drawer closes.
 *
 * The bug: Horizon's cart drawer squeezes the page rather than overlaying it,
 * so opening it at 1000px narrows the content area to ~520px. The header's
 * overflow-list correctly decides the nav no longer fits and collapses it to a
 * hamburger. Closing the drawer restores the width — but by then the menu is
 * display:none, and a ResizeObserver on a hidden element never fires. Nothing
 * can measure, so nothing tells it to expand again and the header stays in its
 * mobile layout until a full reload. It also shrinks the header from 66px to
 * 60px, which shifts the whole page.
 *
 * Horizon's own code flags the hazard in header.js #updateMenuVisibility:
 * "Once the menu hides, the overflow-list is display:none and can't measure to
 * clear it." Its mitigation only runs on an event the hidden list can no longer
 * dispatch, so the state never recovers on its own.
 *
 * The fix: watch the page wrapper — which stays visible and therefore keeps
 * reporting — and when its width grows back at desktop sizes, put the menu back
 * on. That makes the overflow-list measurable again; if the items genuinely
 * don't fit, Horizon collapses it a frame later of its own accord. So this
 * nudges the theme rather than overriding its decision.
 */

(() => {
  'use strict';

  if (!('ResizeObserver' in window)) return;

  const header = document.querySelector('#header-component');
  const wrapper = document.querySelector('.page-wrapper');
  if (!header || !wrapper) return;

  const DESKTOP_MIN = 750; // below this the drawer is the correct layout
  const GROWTH_THRESHOLD = 8; // ignore sub-pixel and scrollbar-width jitter

  let lastWidth = wrapper.clientWidth;

  function restoreMenu() {
    if (window.innerWidth < DESKTOP_MIN) return;
    if (header.dataset.menuStyle !== 'drawer') return;

    // Cleared explicitly: the list latched this while squeezed and cannot
    // clear it itself while hidden.
    header.querySelector('overflow-list')?.removeAttribute('minimum-reached');
    header.dataset.menuStyle = 'menu';
  }

  new ResizeObserver(() => {
    const width = wrapper.clientWidth;
    if (width > lastWidth + GROWTH_THRESHOLD) restoreMenu();
    lastWidth = width;
  }).observe(wrapper);
})();
